/* eslint-disable max-lines */

import {flatten, map, compact, pickBy, mapValues, fromPairs} from 'lodash';
import getSyncTrackLines from '../getSyncTrackLines';
import {GetSyncPromise} from '../../react/useGetSyncPromise';
import snapTrackPoint from '../snapTrackPoint';
import concatMapboxStyles from '../../mapbox/concatMapboxStyles';
import opacifyMapboxStyle from '../../mapbox/opacifyMapboxStyle';
import {blurOpacity} from './constants';
import {trackFixedLayerIds} from './fixedLayerIds';

interface GetTracksMapboxStyleOptions {
    showTracks: boolean;
    showRoutes: boolean;
    tracks: {[id: string]: Track};
    project: MapProject;
    interaction: MapInteraction;
    getSyncPromise: GetSyncPromise;
    highlight: MapHighlight;
    foldersMembership: MapFoldersMembership;
    isPrinting: boolean;
    user: User | undefined;
}

type LocalFixedLayerIds = typeof trackFixedLayerIds['localUser'];
type RemoteFixedLayerIds = typeof trackFixedLayerIds['remoteUserTracks'];

export default function getStyle(options: GetTracksMapboxStyleOptions) {
    return {
        metadata: {
            uniqueTilecacheKey: 'tracks'
        },
        ...concatMapboxStyles(compact([
            options.showTracks && opacifyMapboxStyle(
                options.highlight ? (options.isPrinting ? 0 : 0.25) : 1,
                getRemoteUserStyle('tracks', trackFixedLayerIds.remoteUserTracks, options)
            ),
            options.showRoutes && opacifyMapboxStyle(
                options.highlight ? (options.isPrinting ? 0 : 0.25) : 1,
                getRemoteUserStyle('routes', trackFixedLayerIds.remoteUserRoutes, options)
            ),
            opacifyMapboxStyle(
                options.highlight ? (options.isPrinting ? 0 : 0.25) : 1,
                getLocalStyle(
                    'localUser',
                    pickBy(options.tracks, track => (
                        !track.properties.isLocallyDeleted &&
                        !isHighlighted(options.highlight, track, options.foldersMembership) &&
                        !track.properties.isPublicTrack
                    )),
                    trackFixedLayerIds.localUser,
                    options
                )
            ),
            getRemoteHighlightStyle(trackFixedLayerIds.remoteHighlight, options),
            getLocalStyle('localHighlight', pickBy(options.tracks, track => (
                !track.properties.isLocallyDeleted &&
                isHighlighted(options.highlight, track, options.foldersMembership)
            )), trackFixedLayerIds.localHighlight, options)
        ]))
    };
}

function isHighlighted(
    highlight: MapHighlight,
    track: Track,
    foldersMembership: MapFoldersMembership
) {
    if (!highlight) {
        return false;
    } else if (highlight.type === 'track') {
        return highlight.id === track.properties.trackLocalId;
    } else if (highlight.type === 'folder') {
        const folder = foldersMembership[highlight.id];
        return folder && folder.tracks[track.properties.trackLocalId];
    } else {
        return false;
    }
}

function getLocalStyle(
    uniqueTilecacheKey: string,
    tracks: Record<string, Track>,
    layerIds: LocalFixedLayerIds,
    {interaction, project, getSyncPromise, ...options}: GetTracksMapboxStyleOptions
): mapboxgl.Style | undefined {

    if (!project || (!options.showRoutes && !options.showTracks)) return undefined;

    tracks = pickBy(tracks, track => (
        options.showRoutes === (track.properties.track_type === 'route') ||
        options.showTracks === (track.properties.track_type === '')
    ));

    const lines = flatten(compact(map(tracks, track =>
        getSyncTrackLines({
            track,
            project,
            interaction,
            getSyncPromise,
            includeAppendPreviewLine: true
        })[0]
    )));

    const editingRouteLocalId = interaction.type === 'editingRoute' ?
        interaction.trackLocalId :
        undefined;

    const editingRoute = editingRouteLocalId && tracks[editingRouteLocalId];

    const points: TrackPoint[] = editingRoute && editingRoute.isGeometryLoaded ?
        map(editingRoute.points, point => ({
            ...point,
            geometry: getSyncPromise(snapTrackPoint({
                point,
                track: editingRoute,
                project,
                interaction
            }), point.geometry)[0]
        })) : [];

    if (lines.length === 0 && points.length === 0) return;

    const lineColorExpression: mapboxgl.Expression = [
        'get',
        ['get', 'trackLocalId'],
        ['literal', mapValues(tracks, track => track.properties.color)]
    ];

    return {
        version: 8,
        metadata: {
            uniqueTilecacheKey,
        },
        sources: {
            lines: {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: lines
                },
            },
            points: {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: points,
                },
            },
        },
        layers: compact([
            getCasingLayer({id: layerIds.casing, interaction}),

            getLineLayer({
                id: layerIds.normal,
                interaction,
                filter: ['all', ['==', ['get', 'type'], 'normal'], ['get', 'isGeometryLoaded']],
                paint: {'line-color': lineColorExpression},
            }),
            getLineLayer({
                id: layerIds.preview,
                interaction,
                filter: ['all', ['==', ['get', 'type'], 'preview'], ['get', 'isGeometryLoaded']],
                paint: {
                    'line-dasharray': [1, 1.25],
                    'line-color': lineColorExpression,
                }
            }),
            getLineLayer({
                id: layerIds.loading,
                interaction,
                filter: ['!', ['get', 'isGeometryLoaded']],
                paint: {
                    'line-dasharray': [0, 1.25],
                    'line-color': lineColorExpression,
                }
            }),
            getRouteSymbolLayer({
                id: layerIds['route-symbol'],
                interaction,
                filter: ['all', ['==', ['get', 'type'], 'normal'], ['get', 'isGeometryLoaded']],
            }),
            editingRouteLocalId && {
                id: layerIds.point,
                type: 'circle',
                source: 'points',
                filter: ['all',
                    ['==', ['get', 'trackLocalId'], editingRouteLocalId || null],
                    ['!', ['get', 'isLocallyDeleted']]
                ],
                paint: {
                    'circle-color': options.tracks[editingRouteLocalId].properties.color,
                    'circle-radius': 11,
                    'circle-stroke-color': '#fff',
                    'circle-stroke-width': 1,
                }
            },
        ])
    };
}

function getRemoteHighlightStyle(layerIds: RemoteFixedLayerIds, {
    tracks,
    interaction,
    highlight,
}: GetTracksMapboxStyleOptions): mapboxgl.Style | undefined {

    const source = getRemoteHighlightSource(highlight);
    if (!source) return;

    const filter = getRemoteFilter(tracks);

    return {
        version: 8,
        metadata: {
            uniqueTilecacheKey: 'remoteHighlight'
        },
        sources: {source},
        layers: compact([
            getCasingLayer({
                id: layerIds.casing,
                interaction,
                source: 'source',
                ...source.type === 'vector' && {'source-layer': 'tracks'},
                filter,
            }),
            getLineLayer({
                id: layerIds.line,
                interaction,
                source: 'source',
                ...source.type === 'vector' && {'source-layer': 'tracks'},
                filter,
            })
        ])
    };
}

function getRemoteHighlightSource(highlight: MapHighlight): mapboxgl.AnySourceData | undefined {
    if (highlight && highlight.type === 'track') {
        return {
            type: 'geojson',
            // eslint-disable-next-line max-len
            data: `${window.location.origin}/api/objects/track/${highlight.id}/?pubLink=${highlight.pubLink}`
        };

    } else if (highlight && highlight.type === 'folder') {
        return {
            type: 'vector',
            // eslint-disable-next-line max-len
            tiles: [`${window.location.origin}/api/tiles/folders/${highlight.id}/{z}/{x}/{y}.mvt?pubLink=${highlight.pubLink}`]
        };
    }
}


function getRemoteUserStyle(layer: 'tracks'|'routes', layerIds: RemoteFixedLayerIds, {
    tracks,
    interaction,
    project,
    highlight,
    user,
}: GetTracksMapboxStyleOptions): mapboxgl.Style | undefined {
    if (!project || !user || !user.isAuthenticated) return undefined;

    const filter = [
        'all',
        getRemoteFilter(tracks),
        highlight && highlight.type === 'track' ?
            ['!=', ['get', 'id'], highlight.id] :
            ['literal', true]
    ];

    return {
        version: 8,
        metadata: {
            uniqueTilecacheKey: `remoteUserTracks__${layer}`
        },
        sources: {
            source: {
                type: 'vector',
                tiles: [`${window.location.origin}/api/tiles/${layer}/{z}/{x}/{y}.mvt`],
                minzoom: 3,
                maxzoom: 14
            }
        },
        layers: [
            getCasingLayer({
                id: layerIds.casing,
                interaction,
                source: 'source',
                'source-layer': 'tracks',
                filter,
            }),
            getLineLayer({
                id: layerIds.line,
                interaction,
                source: 'source',
                'source-layer': 'tracks',
                filter,
            })
        ]
    };
}

function getFocusExpression<T>(
    interaction: MapInteraction,
    focused: T,
    blurred: T,
    normal: T
): T | mapboxgl.Expression {

    return interaction.type === 'editingRoute' ?
        ['case', ['==', ['get', 'trackLocalId'], interaction.trackLocalId], focused, blurred] :
        normal;
}

interface GetCasingLayerOptions extends Partial<mapboxgl.Layer> {
    interaction: MapInteraction;
    id: string;
}

function getCasingLayer({
    interaction,
    paint,
    layout,
    ...layer
}: GetCasingLayerOptions): mapboxgl.Layer {

    return {
        type: 'line',
        source: 'lines',
        ...layer,
        paint: {
            'line-color': '#fff',
            'line-width': getFocusExpression(interaction, 6, 4, 4),
            'line-opacity': getFocusExpression(interaction, 0.7, blurOpacity, 0.9),
            ...paint,
        },
        layout: {
            'line-cap': 'round',
            ...layout,
        },
    };
}

interface GetLineLayerOptions extends Partial<mapboxgl.Layer> {
    interaction: MapInteraction;
    id: string;
}

function getLineLayer({
    paint,
    interaction,
    layout,
    ...layer
}: GetLineLayerOptions): mapboxgl.Layer {

    return {
        type: 'line',
        source: 'lines',
        ...layer,
        paint: {
            'line-color': ['get', 'color'],
            'line-width': getFocusExpression(interaction, 5, 3, 3),
            'line-opacity': getFocusExpression(interaction, 0.6, blurOpacity, 1),
            ...paint,
        },
        layout: {
            'line-cap': 'round',
            ...layout,
        },
    };
}

function getRouteSymbolLayer({
    interaction,
    filter,
    ...layer
}: GetLineLayerOptions): mapboxgl.Layer | undefined {
    if (interaction.type !== 'editingRoute') return;

    return {
        type: 'symbol',
        source: 'lines',
        ...layer,
        layout: {
            'icon-image': 'trailDirectionIcon',
            'icon-size': 0.6,
            'icon-allow-overlap': false,
            'symbol-placement': 'line',
            'symbol-spacing': 80,
            'icon-keep-upright': false,
            'icon-ignore-placement': false,
        },
        paint: {
            'icon-opacity': 0.65,
        },
        filter: [
            'all',
            ['==', ['get', 'trackLocalId'], interaction.trackLocalId],
            filter,
        ]
    };
}

function getRemoteFilter(tracks: Record<string, Track>) {
    return ['all',
        [
            'has',
            ['geometry-type'],
            ['literal', {LineString: true, MultiLineString: true}]
        ],
        ['!', ['has', ['get', 'id'], ['literal', fromPairs(
            map(
                pickBy(tracks, track => track.isGeometryLoaded && !track.properties.isPublicTrack),
                track =>[track.properties.id, true]
            )
        )]]],
    ];
}
